<template>
  <MainLayout>
    <div class="container text-left faq-page">
      <h2 class="title text-uppercase text-left mb-12">{{ copy.title }}</h2>
      <p class="faq-page__intro">{{ copy.intro }}</p>

      <div v-if="isLoading" class="faq-page__status">
        {{ copy.loading }}
      </div>

      <div v-else-if="hasError" class="faq-page__status faq-page__status--error">
        {{ copy.error }}
      </div>

      <div v-else>
        <section
          v-for="section in visibleSections"
          :id="section.id"
          :key="section.id"
          class="faq-page__section"
        >
          <div class="faq-page__section-header">
            <h3>{{ section.title }}</h3>
          </div>

          <div
            v-for="group in section.groups"
            :key="`${section.id}-${group.title}`"
            class="faq-page__group"
          >
            <h4>{{ group.title }}</h4>
            <el-collapse>
              <el-collapse-item
                v-for="item in group.items"
                :key="`${group.title}-${item.question}`"
                :name="item.question"
              >
                <template slot="title">
                  <div class="faq-page__question">{{ item.question }}</div>
                </template>
                <div
                  class="faq-page__answer"
                  v-html="item.answer"
                />
              </el-collapse-item>
            </el-collapse>
          </div>
        </section>

        <div
          v-if="visibleSections.length === 0"
          class="faq-page__status"
        >
          {{ copy.empty }}
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import { mapState } from "vuex";
import MainLayout from "@/components/layout/main-layout.vue";
import { publicFormsContent } from "@/content/public-pages-content";
import {
  getCurrentMarket,
  normalizeLocale,
} from "@/utils/market-utils";

const sectionIdMap = {
  "COMPAGNIE ET SERVICES": "company-services",
  "COMPANY AND SERVICES": "company-services",
  "COMMANDES ET LIVRAISON": "orders-deliveries",
  "ORDERS AND DELIVERIES": "orders-deliveries",
};

function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function escapeHtml(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderAnswer(answer) {
  const escaped = escapeHtml(answer);

  return escaped
    .replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (_match, text, url) => {
        const isExternal = /^https?:\/\//i.test(url) || /^www\./i.test(url);
        const href = isExternal && !url.startsWith("http") ? `https://${url}` : url;
        const external = isExternal ? ' target="_blank" rel="noopener"' : "";
        return `<a href="${href}"${external}>${text}</a>`;
      }
    )
    .replace(
      /((https?:\/\/|www\.)[^\s<]+)/g,
      (match) => {
        const url = match.indexOf("http") === 0 ? match : `https://${match}`;
        return `<a href="${url}" target="_blank" rel="noopener">${match}</a>`;
      }
    )
    .replace(
      /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
      '<a href="mailto:$1">$1</a>'
    )
    .replace(/\n/g, "<br />");
}

function isAnswerAvailable(answer) {
  const normalized = (answer || "").trim().toLowerCase();
  return normalized !== "" && normalized !== "na" && normalized !== "n/a";
}

export default {
  components: {
    MainLayout,
  },
  data() {
    return {
      faqData: [],
      isLoading: true,
      hasError: false,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    market() {
      return getCurrentMarket(this.actionBar && this.actionBar.country);
    },
    localeKey() {
      return normalizeLocale(this.$i18n.locale);
    },
    copy() {
      return publicFormsContent.faq[this.localeKey];
    },
    visibleSections() {
      return this.faqData
        .map((section) => {
          const sectionTitle = this.localeKey === "fr" ? section.sectionFr : section.sectionEn;
          const sectionId =
            sectionIdMap[section.sectionFr] ||
            sectionIdMap[section.sectionEn] ||
            slugify(sectionTitle);

          const groups = section.groups
            .map((group) => {
              const title = this.localeKey === "fr" ? group.groupFr : group.groupEn;
              const items = group.items
                .map((item) => {
                  const localizedAnswer = item.answers[this.market][this.localeKey];
                  const fallbackAnswer = item.answers[this.market][
                    this.localeKey === "fr" ? "en" : "fr"
                  ];
                  const answer = isAnswerAvailable(localizedAnswer)
                    ? localizedAnswer
                    : fallbackAnswer;

                  if (!isAnswerAvailable(answer)) {
                    return null;
                  }

                  return {
                    question:
                      this.localeKey === "fr" ? item.questionFr : item.questionEn,
                    answer: renderAnswer(answer),
                  };
                })
                .filter(Boolean);

              if (items.length === 0) {
                return null;
              }

              return {
                title,
                items,
              };
            })
            .filter(Boolean);

          if (groups.length === 0) {
            return null;
          }

          return {
            id: sectionId,
            title: sectionTitle,
            groups,
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    "$route.hash"() {
      this.scrollToHash();
    },
    visibleSections() {
      this.$nextTick(() => {
        this.scrollToHash();
      });
    },
  },
  mounted() {
    this.fetchFaq();
  },
  methods: {
    async fetchFaq() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const response = await fetch("/content/faq.json");
        this.faqData = await response.json();
      } catch (error) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    scrollToHash() {
      if (!this.$route.hash) {
        return;
      }

      const target = document.querySelector(this.$route.hash);

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.faq-page {
  padding-top: 56px;
  padding-bottom: 72px;
}

.faq-page__intro {
  max-width: 760px;
  line-height: 1.7;
  margin-bottom: 28px;
}

.faq-page__section {
  margin-bottom: 40px;
  scroll-margin-top: 120px;
}

.faq-page__section-header {
  margin-bottom: 16px;
}

.faq-page__section-header,
.faq-page__group {
  text-align: left;
}

.faq-page__group {
  margin-bottom: 22px;

  h4 {
    margin-bottom: 12px;
  }
}

.faq-page__question {
  font-weight: 600;
  white-space: normal;
  line-height: 1.5;
  padding-right: 16px;
}

.faq-page__answer {
  line-height: 1.8;
}

.faq-page__status {
  padding: 18px 20px;
  border-radius: 16px;
  background: #faf8f6;
}

.faq-page__status--error {
  color: $color-primary;
}

@media screen and (max-width: $media-md) {
  .faq-page {
    padding-top: 36px;
    padding-bottom: 48px;
  }
}
</style>
